import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Badge,
  Button,
  Card,
  Choice,
  EmptyState,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@efira/ui';

import { useCatalog } from '../../api/queries';
/* Склонение - общим форматтером продукта (`lib/plural.ts`): своя копия
   рядом с каждым экраном расходится на первом же слове. */
import { games as gamesWord } from '../../lib/plural';
import type { CatalogCategory, CatalogGame } from '../../api/queries';
import { useConsoleSession } from '../session';

/**
 * Каталог игр - центр консоли, когда сессии нет.
 *
 * Состав взят у эталона (`docs/reference/LIVECADE_UX.md`, 5.1): поиск с
 * очисткой, фильтр доступа из трёх кнопок, категории с описанием и счётчиком,
 * сетка плиток, пустое состояние поиска.
 *
 * ДВА ОТСТУПЛЕНИЯ ОТ ЭТАЛОНА, оба сознательные:
 *
 * 1. Нажатие на плитку открывает КАРТОЧКУ С ОПИСАНИЕМ, а не запускает игру
 *    сразу. У эталона запуск немедленный, и это дёшево для него: игра всё
 *    равно идёт только в превью. У нас выбор игры меняет сцену overlay,
 *    который может быть уже открыт в OBS, - промах мышью не должен менять
 *    картинку в браузерном источнике. Решение владельца.
 * 2. В карточке нет видео-предпросмотра: роликов у нас пока нет, а пустой
 *    прямоугольник с надписью «скоро» - обещание, а не функциональность.
 *
 * ПЛАТЁЖНОЙ СТЕНЫ НА ВЫБОРЕ НЕТ. Платная игра выбирается и играет в кадре
 * целиком; тариф спрашивают при выходе в эфир (`/api/live/connect`).
 */
type Access = 'all' | 'free' | 'pro';

/**
 * Фильтр доступа.
 *
 * «Pro», а не «Платные»: тариф называется Pro во всех остальных местах
 * консоли - в промо левой колонки, в меню аккаунта, в подсказке короны. Два
 * названия одного тарифа в одном кадре читаются как два разных тарифа.
 *
 * Счётчик - отдельным бейджем, а не частью подписи: «Бесплатные 1» подписью
 * быть перестаёт, а при росте каталога числа разъезжают кнопки по ширине.
 */
const ACCESS: ReadonlyArray<{ id: Access; label: string }> = [
  { id: 'all', label: 'Все' },
  { id: 'free', label: 'Бесплатные' },
  { id: 'pro', label: 'Pro' },
];

function ruTitle(value: Record<string, string>, fallback: string): string {
  return value['ru'] ?? value['en'] ?? fallback;
}

/** Размеры вариантов обложки. Те же числа, что у сборщика `tools/covers`. */
const COVER_SIZE = {
  tile: { width: 277, height: 185 },
  card: { width: 144, height: 96 },
} as const;

/**
 * Обложка игры.
 *
 * ДВЕ ВЕЩИ, РАДИ КОТОРЫХ ЭТО ОТДЕЛЬНЫЙ КОМПОНЕНТ.
 *
 * 1. Размер под фактический показ. Браузер выбирает по `srcset` между
 *    одинарной и двойной плотностью; один большой файл на все случаи стоил
 *    каталогу 4,2 МБ при первом открытии.
 * 2. Запасной вид. Обложки может не быть - до выгрузки в CDN и у новой игры,
 *    добавленной раньше своей картинки. Тогда плитка показывает плашку с
 *    названием: каталог остаётся пригодным, а недостача видна честно, а не
 *    притворяется поломкой вёрстки.
 *
 * Ширина и высота проставлены всегда: без них плитки прыгают, пока грузятся
 * картинки, и нажатие уезжает из-под курсора.
 */
function Cover({
  game,
  kind,
  className,
}: {
  game: CatalogGame;
  kind: keyof typeof COVER_SIZE;
  className: string;
}) {
  const [failed, setFailed] = useState(false);
  const size = COVER_SIZE[kind];

  if (failed) {
    return (
      <span
        data-catalog-cover-fallback
        className={`bg-neutral text-neutral-content flex items-center justify-center px-3 text-center text-xs ${className}`}
      >
        {ruTitle(game.title, game.id)}
      </span>
    );
  }

  return (
    <img
      src={game.cover[kind]}
      srcSet={`${game.cover[kind]} 1x, ${game.cover[`${kind}@2x`]} 2x`}
      width={size.width}
      height={size.height}
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => {
        setFailed(true);
      }}
      className={className}
    />
  );
}

export function Catalog() {
  const catalog = useCatalog();
  const session = useConsoleSession();
  const [query, setQuery] = useState('');
  const [access, setAccess] = useState<Access>('all');
  const [chosen, setChosen] = useState<CatalogGame | null>(null);

  /*
   * Фокус ведётся руками в трёх местах, и каждое - починка настоящей потери.
   *
   * Кнопка очистки размонтируется в момент нажатия на неё же, карточка
   * появляется в конце разметки, а закрывается кнопкой внутри себя. Во всех
   * трёх случаях браузер отдаёт фокус `<body>`: пользователь клавиатуры
   * оказывается в начале документа без объяснения. Проверено аудитом
   * 2026-09-19 - axe этого класса дефектов не видит в принципе.
   */
  const searchRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLButtonElement | null>(null);

  const closeCard = useCallback(() => {
    setChosen(null);
    openerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (chosen === null) return;

    cardRef.current?.focus();
  }, [chosen]);

  /* Escape закрывает карточку: она ведёт себя как шаг выбора и обязана
     отменяться тем же способом, что и любое окно. */
  useEffect(() => {
    if (chosen === null) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeCard();
    };

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [chosen, closeCard]);

  const games = useMemo(
    () => (catalog.data?.games ?? []).filter((game) => game.status === 'ready'),
    [catalog.data],
  );
  const categories = catalog.data?.categories ?? [];

  /**
   * Поиск идёт по названию И по категории: «викторина» обязана находить
   * «Знатока», хотя этого слова нет в его названии. Поиск только по названию
   * заставляет помнить точное имя игры - то есть знать ответ заранее.
   */
  const needle = query.trim().toLowerCase();
  const byCategory = new Map(categories.map((item) => [item.id, item]));

  const found = games.filter((game) => {
    if (access === 'free' && !game.free) return false;
    if (access === 'pro' && game.free) return false;
    if (needle === '') return true;

    const category = byCategory.get(game.category);

    return (
      ruTitle(game.title, game.id).toLowerCase().includes(needle) ||
      ruTitle(game.tagline, '').toLowerCase().includes(needle) ||
      (category !== undefined && ruTitle(category.title, '').toLowerCase().includes(needle))
    );
  });

  const counts = ACCESS.map((item) => ({
    ...item,
    count: games.filter(
      (game) => item.id === 'all' || (item.id === 'free' ? game.free : !game.free),
    ).length,
  }));

  return (
    <div data-console-mode="catalog" className="space-y-7 px-6 py-6">
      <div
        data-control-row="catalog-filters"
        className="@container flex flex-wrap items-center gap-6"
      >
        {/* ПОЛЕ СО ЗНАЧКОМ - `InputGroup`, а не поле внутри рамки.
            Было «рамка в рамке»: класс `input` FlyonUI рисовал внешнюю
            рамку, а поле пресета внутри - свою (находка владельца
            2026-09-25). */}
        <InputGroup className="min-w-52 flex-1 @max-[300px]:min-w-full">
          <InputGroupAddon>
            <Icon name="search" size="sm" />
          </InputGroupAddon>
          <InputGroupInput
            ref={searchRef}
            /*
              Именно text, а не search: у `type="search"` браузер рисует
              СВОЙ крестик очистки, и рядом с нашим их получалось два -
              неясно, который что чистит.
            */
            type="text"
            data-catalog-search
            value={query}
            placeholder="Поиск по играм"
            aria-label="Поиск по играм"
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
          {/* Кнопка очистки появляется только при непустом поле - как у эталона. */}
          {query !== '' && (
            <InputGroupAddon align="inline-end">
              <Button
                variant="ghost"
                size="s"
                iconOnly
                data-catalog-search-clear
                /* Не разрушающее: стирает только набранный запрос. */
                data-not-destructive="поле поиска"
                aria-label="Очистить поиск"
                onClick={() => {
                  setQuery('');
                  /* Кнопка исчезает вместе с запросом - фокус возвращается
                     в поле, где ввод и продолжается. */
                  searchRef.current?.focus();
                }}
              >
                <Icon name="dismiss" size="sm" />
              </Button>
            </InputGroupAddon>
          )}
        </InputGroup>

        {/*
          ФИЛЬТР ДОСТУПА - `Choice`, роль «набор вариантов».
          Было своё: группа `join` FlyonUI и выбранный сегмент, покрашенный
          по месту (`bg-border`). Именно из-за таких копий одна роль
          выглядела по-разному в разных местах.
        */}
        <Choice
          label="Доступ"
          data-catalog-access
          size="sm"
          value={access}
          onChange={setAccess}
          options={counts.map((item) => ({
            value: item.id,
            data: { 'data-catalog-access-item': item.id },
            label: (
              <>
                {item.label}
                {/* Счётчик - рядом с подписью: он и есть смысл фильтра. */}
                <span className="ms-2 tabular-nums">{item.count}</span>
              </>
            ),
          }))}
        />
      </div>

      {catalog.isLoading && <p className="text-foreground/80 text-sm">Каталог загружается…</p>}

      {/*
        Смена выдачи объявляется вслух. Без этого поиск для экранного диктора
        нем: список сужается молча, и «ничего не нашлось» читатель узнаёт,
        только дойдя до сообщения табом.

        Без двоеточия: «Найдено: 10 игр» - та самая пара «слово: число»,
        которую запрещают ворота `console-plural` (для чтеца правило то же).
        «В выдаче» согласуется с любым числом: 1 игра, 2 игры, 10 игр
        (u23b, решение владельца 2026-09-23).
      */}
      <p data-catalog-live role="status" aria-live="polite" className="sr-only">
        {catalog.isLoading
          ? 'Каталог загружается'
          : found.length === 0
            ? 'Ничего не найдено'
            : `В выдаче ${gamesWord(found.length)}`}
      </p>

      {!catalog.isLoading && found.length === 0 && (
        <CatalogEmpty query={query} access={access} categories={categories} />
      )}

      {categories.map((category) => {
        const inCategory = found.filter((game) => game.category === category.id);

        if (inCategory.length === 0) return null;

        return (
          <section key={category.id} aria-labelledby={`cat-${category.id}`}>
            <CategoryHead category={category} shown={inCategory.length} />
            <div className="mt-3 grid grid-cols-[repeat(auto-fill,minmax(210px,1fr))] gap-3">
              {inCategory.map((game) => (
                <Tile
                  key={game.id}
                  game={game}
                  category={ruTitle(category.title, category.id)}
                  onOpen={(element) => {
                    openerRef.current = element;
                    setChosen(game);
                  }}
                />
              ))}
            </div>
          </section>
        );
      })}

      {chosen !== null && (
        <GameCard
          ref={cardRef}
          game={chosen}
          category={ruTitle(byCategory.get(chosen.category)?.title ?? {}, chosen.category)}
          onClose={closeCard}
          onChoose={() => {
            session.start(chosen.id);
            setChosen(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * Заголовок категории: название, описание одной строкой, счётчик.
 *
 * Счётчик берётся из ответа сервера, а при поиске показывает найденное из
 * общего - иначе цифра в заголовке спорит с числом плиток под ним.
 */
function CategoryHead({ category, shown }: { category: CatalogCategory; shown: number }) {
  return (
    <div data-catalog-category className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {/* Значок и разделитель - как у эталона: заголовок секции обязан
          читаться заголовком, а не первой строкой текста.

          РАЗДЕЛИТЕЛЬ - РАМКА ОПИСАНИЯ, А НЕ ОТДЕЛЬНЫЙ УЗЕЛ (N-15). Отдельной
          чертой он оставался в конце строки заголовка, когда описание
          переносилось: «Команда на команду |». Рамка едет вместе с
          описанием и стоит перед ним на любой строке. */}
      <Icon name="sparkle" size="sm" tone="accent" />
      <h3 id={`cat-${category.id}`} className="text-foreground text-lg font-semibold">
        {ruTitle(category.title, category.id)}
      </h3>
      <p className="text-foreground/80 border-foreground/20 grow border-s ps-3 text-sm">
        {ruTitle(category.description, '')}
      </p>
      <span className="text-foreground/80 text-sm whitespace-nowrap">
        {shown === category.count ? gamesWord(category.count) : `${shown} из ${category.count}`}
      </span>
    </div>
  );
}

/**
 * Плитка игры.
 *
 * Обложка 3:2 - измеренная пропорция плитки эталона. Название, категория и
 * корона Pro стоят ПОД обложкой, а не поверх: поверх они спорят с афишей, а
 * афиша - главное, ради чего обложка рисовалась.
 */
/** Сколько держать курсор, прежде чем показать карточку. */
const HOVER_DELAY_MS = 350;

/**
 * Сколько меток помещается в один ряд карточки.
 *
 * Три: «Бесплатно» плюс две из того, чем играют. Остальные сворачиваются в
 * «+N» - высота карточки при этом не меняется, а число скрытого видно.
 */
const TAGS_SHOWN = 3;

function Tile({
  game,
  category,
  onOpen,
}: {
  game: CatalogGame;
  category: string;
  /* Плитка отдаёт саму себя: на неё возвращается фокус после закрытия
     карточки, и искать её потом по идентификатору незачем. */
  onOpen: (element: HTMLButtonElement) => void;
}) {
  /*
   * КАРТОЧКА ПРИ НАВЕДЕНИИ - С ЗАДЕРЖКОЙ.
   *
   * Без задержки карточка выскакивает у каждой плитки, через которую курсор
   * проехал по дороге, и перекрывает ту, к которой человек шёл. Задержка -
   * это и есть разница между «навёл» и «проехал мимо».
   */
  const [hovered, setHovered] = useState(false);
  const timer = useRef<number | null>(null);

  const show = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);

    timer.current = window.setTimeout(() => {
      setHovered(true);
    }, HOVER_DELAY_MS);
  };

  const hide = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);

    timer.current = null;
    setHovered(false);
  };

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  return (
    /*
     * ПЛИТКА КАТАЛОГА - роль «плитка» из таблицы ролей: `Card`.
     *
     * `asChild` нужен, чтобы под карточкой был настоящий `button`: плитка
     * целиком нажимается и должна доходить с клавиатуры. `Button` пресета
     * сюда не годится - он строка с выравниванием по центру и фиксированной
     * высотой, и плитки налезали друг на друга (ворота каталога поймали это
     * первым же прогоном: «subtree intercepts pointer events»).
     *
     * ВИД ПЛИТКИ - КАРТОЧКА ПРЕСЕТА, БЕЗ ПОПРАВОК. Прежде она была без
     * фона и рамки «как у эталона»; теперь у неё граница карточки, и это
     * ровно то, чего не хватало владельцу: видно, где плитка начинается и
     * где кончается. Довод «так у эталона» с единицы 13 не принимается.
     */
    <Card asChild size="sm" className="group relative gap-0 py-0 pb-2 text-left">
      {/* eslint-disable-next-line no-restricted-syntax -- `asChild` требует
          голого элемента: это и есть тот самый случай, ради которого он у
          компонента есть. Роль остаётся за Card. */}
      <button
        type="button"
        data-catalog-tile={game.id}
        onClick={(event) => {
          onOpen(event.currentTarget);
        }}
        onPointerEnter={show}
        onPointerLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        <span className="bg-border relative block aspect-3/2 overflow-hidden rounded-lg">
          <Cover
            game={game}
            kind="tile"
            className="size-full object-cover transition group-hover:scale-[1.03]"
          />
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/35 opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100">
            <Icon name="play" size="xl" tone="inverse" />
          </span>

          {/* Значок каталога. Правило - числа на сервере (`world/badges.ts`):
            NEW - последняя волна выпуска, POPULAR - доля минут эфира за 30
            дней. Значок, расставленный на глаз, врёт в первый же месяц. */}
          {game.badge !== null && (
            <Badge
              data-catalog-badge={game.badge}
              variant={game.badge === 'new' ? 'default' : 'secondary'}
              className="absolute start-1.5 top-1.5"
            >
              {game.badge === 'new' ? 'НОВОЕ' : 'ПОПУЛЯРНОЕ'}
            </Badge>
          )}
        </span>

        <span className="flex items-start gap-2 px-1 pt-3">
          <span className="min-w-0 grow">
            <span className="text-foreground block truncate text-sm font-medium">
              {ruTitle(game.title, game.id)}
            </span>
            <span className="text-foreground/80 block truncate text-xs">{category}</span>
          </span>

          {!game.free && (
            <span
              data-catalog-tile-crown
              title="Игра тарифа Pro: на оверлее доступна целиком, в эфир выходит на Pro"
              className="text-warning shrink-0"
            >
              <Icon name="crown" size="sm" />
              <span className="sr-only">Игра тарифа Pro</span>
            </span>
          )}
        </span>

        {hovered && <HoverPanel game={game} category={category} />}
      </button>
    </Card>
  );
}

/**
 * Карточка при наведении: название, жанр, описание, метки и предпросмотр.
 *
 * ПРЕДПРОСМОТР ЛЕНИВЫЙ И ТОЛЬКО ПО НАВЕДЕНИЮ. Разметки ролика до наведения
 * нет вовсе - значит в первую загрузку каталога он не входит по построению,
 * а не «по настройке `preload`». Ролика нет ни у одной игры (`preview`
 * приходит `null`) - тогда карточка показывает обложку, а не пустой
 * прямоугольник с надписью «скоро».
 */
function HoverPanel({ game, category }: { game: CatalogGame; category: string }) {
  /*
   * ТЕГИ - СЛОВАМИ СТРИМЕРА, А НЕ ПЕРЕЧИСЛЕНИЯМИ КОДА.
   *
   * Здесь висели «DOM», «Холст», «3D», «Счёт», «Фигуры», «Сюжет» -
   * способ рисования сцены и класс мира. Это внутренние понятия: они
   * ничего не говорят человеку, а рядом с «Бесплатно» читаются как
   * свойство игры (находка владельца, единица 8). Теперь тег отвечает на
   * вопрос «чем в это играют»: список приходит с сервера из заводских
   * привязок игры.
   */
  const tags = [game.free ? 'Бесплатно' : 'Pro', ...game.plays];

  return (
    <span
      data-catalog-hovercard={game.id}
      role="presentation"
      /* Карточка ПЕРЕКРЫВАЕТ соседей - как у эталона: иначе она либо режется
         колонкой, либо раздвигает сетку, и плитки уезжают из-под курсора. */
      className="bg-background border-foreground/15 pointer-events-none absolute start-0 top-full z-20 mt-1 block w-64 rounded-lg border p-3 text-left shadow-xl"
    >
      <span className="bg-border mb-2 block aspect-3/2 overflow-hidden rounded">
        {game.preview === null ? (
          <img
            src={game.cover['card']}
            srcSet={`${game.cover['card']} 1x, ${game.cover['card@2x']} 2x`}
            width={288}
            height={192}
            alt=""
            decoding="async"
            className="size-full object-cover"
          />
        ) : (
          <video
            data-catalog-preview={game.id}
            src={game.preview}
            poster={game.cover['card']}
            /* `preload="none"` - вторая линия: сама разметка появляется
               только сейчас, но и появившись, ролик не тянется, пока не
               начнёт играть. */
            preload="none"
            autoPlay
            muted
            loop
            playsInline
            className="size-full object-cover"
          />
        )}
      </span>

      <span className="text-foreground block text-sm font-semibold">
        {ruTitle(game.title, game.id)}
      </span>
      <span data-catalog-hovercard-genre className="text-foreground/80 block text-xs">
        {category}
      </span>
      {/*
        ОПИСАНИЕ ЗАКАНЧИВАЕТСЯ ЗНАКОМ КОНЦА - ТОЧКОЙ ИЛИ МНОГОТОЧИЕМ.

        Находка владельца: описание обрывалось ничем, и понять, кончилось
        оно или его срезали, было нельзя. Правило простое: помещается
        целиком - точка (она стоит в самом описании, `packages/games`);
        не помещается - многоточие, и его рисует `line-clamp`, а не мы.
        Две строки, а не «сколько выйдет»: высота карточки постоянна, иначе
        карточка перекрывает соседний ряд сетки по-разному у разных игр.
      */}
      <span data-catalog-hovercard-text className="text-foreground/90 mt-1 line-clamp-2 text-xs">
        {ruTitle(game.tagline, '')}
      </span>

      {/*
        ЧИСЛО МЕТОК ОГРАНИЧЕНО, ЛИШНЕЕ СВОРАЧИВАЕТСЯ.

        Находка владельца: карточка выросла до двух рядов чипов и стала
        выше самой плитки. Перенос на второй ряд здесь - не «чуть больше
        текста», а другая карточка: она перекрывает соседний ряд сетки.
        Поэтому меток не больше `TAGS_SHOWN`, а остаток сворачивается в
        «+N» - счёт виден, высота постоянна.
      */}
      <span data-catalog-hovercard-tags className="mt-2 flex flex-nowrap gap-1 overflow-hidden">
        {tags.slice(0, TAGS_SHOWN).map((tag) => (
          <span
            key={tag}
            className="border-foreground/15 bg-muted text-foreground/90 shrink-0 rounded-full px-2 py-1 text-xs whitespace-nowrap"
          >
            {tag}
          </span>
        ))}

        {tags.length > TAGS_SHOWN && (
          <span
            data-catalog-hovercard-more
            title={tags.slice(TAGS_SHOWN).join(', ')}
            className="border-foreground/15 text-foreground/80 shrink-0 rounded-full border border-dashed px-2 py-1 text-xs whitespace-nowrap"
          >
            +{tags.length - TAGS_SHOWN}
          </span>
        )}
      </span>
    </span>
  );
}

/**
 * Карточка игры с описанием и кнопкой выбора.
 *
 * Про платную игру здесь сказано ровно то, что есть: в кадре доступна
 * целиком, тариф спросят при выходе в эфир. Отказ, о котором предупредили
 * заранее, - это условие; отказ в момент нажатия - это стена.
 */
function GameCard({
  ref,
  game,
  category,
  onClose,
  onChoose,
}: {
  ref: React.Ref<HTMLDivElement>;
  game: CatalogGame;
  category: string;
  onClose: () => void;
  onChoose: () => void;
}) {
  return (
    <div
      ref={ref}
      data-catalog-card
      /* Карточка принимает фокус: она появляется в конце разметки, и без
         переноса до её кнопок пришлось бы протабулировать весь каталог. */
      tabIndex={-1}
      role="group"
      aria-label={`Игра ${ruTitle(game.title, game.id)}`}
      className="border-primary/40 bg-muted sticky bottom-0 z-10 rounded-lg border p-4 shadow-lg outline-none"
    >
      <div className="flex flex-wrap items-start gap-4">
        <Cover
          game={game}
          kind="card"
          className="bg-border h-24 w-36 shrink-0 rounded object-cover"
        />

        <div className="min-w-48 grow">
          <h3 className="text-foreground text-base font-semibold">
            {ruTitle(game.title, game.id)}
          </h3>
          <p className="text-foreground/80 text-sm">{category}</p>
          {/* В нижней карточке описание показывается ЦЕЛИКОМ: места хватает,
              и обрезать там нечего - но знак конца тот же. */}
          <p data-catalog-card-text className="text-foreground mt-2 text-sm">
            {ruTitle(game.tagline, '')}
          </p>

          {!game.free && (
            <p className="text-foreground mt-2 flex items-start gap-2 text-sm">
              <Icon name="crown" size="sm" tone="warning" className="mt-1" />
              На оверлее игра доступна целиком. Чтобы её увидели зрители, нужен тариф Pro.
            </p>
          )}
        </div>

        <div className="flex shrink-0 gap-2">
          <Button variant="ghost" size="s" onClick={onClose}>
            Закрыть
          </Button>
          <Button variant="primary" size="s" data-catalog-choose onClick={onChoose}>
            Выбрать игру
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Пустое состояние поиска: объясняет, что искать, а не просто разводит руками. */
function CatalogEmpty({
  query,
  access,
  categories,
}: {
  query: string;
  access: Access;
  categories: CatalogCategory[];
}) {
  return (
    <EmptyState
      data-catalog-empty
      icon="search"
      title={query === '' ? 'В этом фильтре игр нет' : `По запросу „${query}“ ничего не нашлось`}
    >
      {/* Названия категорий подставляются те же, что в заголовках секций:
          подсказка, называющая несуществующее слово, хуже отсутствующей. */}
      Искать можно по названию игры — «Заезд», «Дуэль» — или по категории:{' '}
      {categories.map((item) => `«${ruTitle(item.title, item.id)}»`).join(', ')}.
      {access !== 'all' && ' Фильтр доступа сейчас сужает выдачу — попробуйте «Все».'}
    </EmptyState>
  );
}
